import { createElement } from 'react';

import { includedSlides } from './include.js';
import { Slide01Intro } from './Slide01Intro.js';
import { Slide02Duration } from './Slide02Duration.js';
import { Slide03Tracks } from './Slide03Tracks.js';
import { Slide04TopArtist } from './Slide04TopArtist.js';
import { Slide05TopTracks } from './Slide05TopTracks.js';
import { Slide06Djs } from './Slide06Djs.js';

import type { WrappedSlideCandidate } from './include.js';
import type { WrappedSlideSpec } from '../index.js';
import type { DjPage } from './Slide06Djs.js';
import type { PartyWrapped } from '@mixwire/contracts';

const DJS_PER_PAGE = 5;

const DJS_BLOBS: WrappedSlideSpec['blobs'] = [
  { paint: 'green', x: '-46cqw', y: '82cqh', scale: 0.9 },
  { paint: 'blue', x: '58cqw', y: '-50cqh', scale: 0.6 },
  { paint: 'red', x: '-56cqw', y: '-54cqh', scale: 0.3 },
];

const djPages = (wrapped: PartyWrapped): readonly DjPage[] => {
  const count = Math.ceil(wrapped.djs.length / DJS_PER_PAGE);

  return Array.from({ length: count }, (_unused, index) => ({
    index,
    count,
    djs: wrapped.djs.slice(index * DJS_PER_PAGE, (index + 1) * DJS_PER_PAGE),
  }));
};

export const EARLY_SLIDES = (wrapped: PartyWrapped): readonly WrappedSlideSpec[] =>
  includedSlides([
    {
      id: 'intro',
      bg: 'night',
      ink: 'paper',
      durationMs: 7000,
      blobs: [
        { paint: 'red', x: '-42cqw', y: '-36cqh', scale: 0.9 },
        { paint: 'green', x: '46cqw', y: '-4cqh', scale: 0.7 },
        { paint: 'blue', x: '-8cqw', y: '46cqh', scale: 1.35 },
      ],
      content: createElement(Slide01Intro, { wrapped }),
      when: true,
    },
    {
      id: 'duration',
      bg: 'blue',
      ink: 'night',
      durationMs: 7000,
      blobs: [
        { paint: 'night', x: '40cqw', y: '-42cqh', scale: 0.75 },
        { paint: 'red', x: '-48cqw', y: '34cqh', scale: 0.9 },
        { paint: 'paper', x: '44cqw', y: '50cqh', scale: 0.45 },
      ],
      content: createElement(Slide02Duration, { wrapped }),
      when: true,
    },
    {
      id: 'tracks-count',
      bg: 'paper',
      ink: 'night',
      durationMs: 7000,
      blobs: [
        { paint: 'red', x: '-50cqw', y: '-46cqh', scale: 0.6 },
        { paint: 'green', x: '52cqw', y: '42cqh', scale: 0.75 },
        { paint: 'blue', x: '34cqw', y: '-52cqh', scale: 0.35 },
      ],
      content: createElement(Slide03Tracks, { wrapped }),
      when: wrapped.trackCount > 0,
    },
    {
      id: 'top-artist',
      bg: 'red',
      ink: 'night',
      durationMs: 7500,
      blobs: [
        { paint: 'night', x: '0cqw', y: '-62cqh', scale: 1.6 },
        { paint: 'green', x: '-52cqw', y: '52cqh', scale: 0.6 },
        { paint: 'paper', x: '56cqw', y: '32cqh', scale: 0.4 },
      ],
      content: createElement(Slide04TopArtist, { wrapped }),
      when: wrapped.trackCount > 0,
    },
    {
      id: 'top-tracks',
      bg: 'night',
      ink: 'paper',
      durationMs: 8000,
      blobs: [
        { paint: 'red', x: '-72cqw', y: '-22cqh', scale: 0.5 },
        { paint: 'green', x: '72cqw', y: '8cqh', scale: 0.4 },
        { paint: 'blue', x: '0cqw', y: '72cqh', scale: 1 },
      ],
      content: createElement(Slide05TopTracks, { wrapped }),
      when: wrapped.trackCount > 0,
    },
    ...djPages(wrapped).map((page): WrappedSlideCandidate => ({
      id: page.index === 0 ? 'djs' : `djs-${String(page.index + 1)}`,
      bg: 'paper',
      ink: 'night',
      durationMs: 7500,
      blobs: DJS_BLOBS,
      content: createElement(Slide06Djs, { wrapped, page }),
      when: true,
    })),
  ]);
